import {
  createContext,
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

export interface DriversLicenseResult {
  firstName?: string;
  lastName?: string;
  middleName?: string;
  DoB?: string;
  addressLine1?: string;
  city?: string;
  state?: string;
  country?: string;
  expireDate?: string;
  zip?: string;
}

interface ConCatUUIDResult {
  uuid?: string;
}

interface ConCatUIDResult {
  userId?: string;
}

interface PlainStringResult {
  value?: string;
}

export type ScannerResult =
  | ConCatUIDResult
  | ConCatUUIDResult
  | DriversLicenseResult
  | PlainStringResult;

export interface Scanner {
  info: SerialPortInfo;
  port?: SerialPort;
}

function portInfoEquals(a: SerialPortInfo, b: SerialPortInfo) {
  return a.usbVendorId === b.usbVendorId && a.usbProductId === b.usbProductId;
}

type CallbackScan = (data: ScannerResult) => void;

interface ScannerContextProps {
  scanners: Scanner[];
  scanListeners: CallbackScan[];
  setListeners: Dispatch<SetStateAction<CallbackScan[]>>;
  readonly addScanner: (port: SerialPort) => void;
  readonly removeScanner: (port: SerialPortInfo) => void;
}

/*
 * Sometimes the date is in MMDDYYYY format, but we want YYYYMMDD format.
 * We check if the 5 and 6th characters are greater than 12, and if so, we assume it's in MMDDYYYY format.
 */
function normalizeDate(date: string): string {
  if (!date) {
    return date;
  }

  const yearSlice = date.slice(4, 6);

  if (yearSlice > '12') {
    return date.slice(4, 8) + date.slice(0, 4);
  }

  return date;
}

function parseScannerData(data: string): ScannerResult {
  if (!data.startsWith('@')) {
    return { value: data.trim() } as PlainStringResult;
  }

  if (data.startsWith('@CONCAT;UUID')) {
    const uuid = data.replace('@CONCAT;UUID', '').trim();
    return { uuid } as ConCatUUIDResult;
  }

  if (data.startsWith('@CONCAT;UID')) {
    const userId = data.replace('@CONCAT;UID', '').trim();
    return { userId } as ConCatUIDResult;
  }

  const kv: Record<string, string> = {};

  for (const entry of data.split('\n')) {
    if (entry.length < 3) {
      continue;
    }

    kv[entry.slice(0, 3)] = entry.slice(3);
  }

  return {
    firstName: kv['DAC']?.trim() ?? kv['DCT']?.trim(),
    middleName: kv['DAD']?.trim(),
    DoB: kv['DBB'] ? normalizeDate(kv['DBB']) : undefined,
    lastName: kv['DCS']?.trim() ?? kv['DAB']?.trim(),
    addressLine1: kv['DAG']?.trim(),
    city: kv['DAI']?.trim(),
    state: kv['DAJ'],
    country: kv['DCG'],
    expireDate: kv['DBA'] ? normalizeDate(kv['DBA']) : undefined,
    zip: kv['DAK']?.trim(),
  } as DriversLicenseResult;
}

async function runScanner(device: Scanner, onScan: (data: ScannerResult) => void): Promise<void> {
  let data = '';
  const { port } = device;

  if (!port) {
    return;
  }

  // From the example: https://wicg.github.io/serial/#readable-attribute
  while (port.readable) {
    const reader = port.readable.getReader();

    try {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done) {
          // |reader| has been canceled.
          break;
        }

        data += new TextDecoder().decode(value);

        if (
          (value.at(-1) === 13 || data.startsWith('@CONCAT;') || !data.startsWith('@')) &&
          data.trim().length > 0
        ) {
          onScan(parseScannerData(data));
          data = '';
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      reader.releaseLock();
    }
  }
}

export function parseDriversLicenseResult(dl: DriversLicenseResult): string {
  let parts = `@ANSI;DBA${dl.expireDate!};DBB${dl.DoB!};DCS${dl.lastName!}`;

  if (dl.middleName) {
    parts += `;DAD${dl.middleName}`;
  }

  if (dl.firstName) {
    parts += `;DAC${dl.firstName}`;
  }

  return parts;
}

export function useScannerResult(onScan: CallbackScan, deps: readonly unknown[]): void {
  const context = useContext(ScannerContext);

  useEffect(() => {
    if (!context) {
      return;
    }

    const { setListeners } = context;
    setListeners((listeners) => [...listeners, onScan]);

    return () => {
      setListeners((listeners) => listeners.filter((listener) => listener !== onScan));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

export const ScannerContext = createContext<ScannerContextProps | undefined>(undefined);

export const useCreateScannerContext = (): ScannerContextProps => {
  const [scanListeners, setScanListeners] = useState<CallbackScan[]>([]);
  // Load scanners from local storage - so page can be refreshed without losing scanner state
  const [devices, setDevices] = useState<Scanner[]>(() => {
    const stored = window.localStorage.getItem('scanner');
    if (stored) {
      try {
        const parse = JSON.parse(stored) as SerialPortInfo[];
        if (Array.isArray(parse)) {
          return parse.map((info) => ({ info }));
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      }
    }

    return [];
  });

  // Load scanner
  const addScanner = useCallback(
    async (d: SerialPort) => {
      await d.open({ baudRate: 9600 });
      const info = d.getInfo();
      const newDevice: Scanner = { port: d, info };

      // Scanner might be already present on the list - remove them first, then re-add them
      setDevices((scanners) => {
        const clone = scanners.filter((t) => !portInfoEquals(t.info, info));
        return [...clone, newDevice];
      });

      await runScanner(newDevice, (result) => {
        // using setScanListeners as a work-around for getting the latest listeners value
        setScanListeners((listeners) => {
          for (const event of listeners) {
            event(result);
          }

          return listeners;
        });
      });
    },
    [setDevices],
  );

  // Save device state into local storage, to be re-used after page refresh
  useEffect(() => {
    window.localStorage.setItem('scanner', JSON.stringify(devices.map((t) => t.info)));
  }, [devices]);

  // Called once during load time to connect all ports
  useEffect(() => {
    let loading = true;
    async function loadPorts() {
      const newPorts = await navigator.serial?.getPorts();
      if (loading && newPorts) {
        // Get saved scanners from localStorage
        const stored = window.localStorage.getItem('scanner');
        let savedScanners: SerialPortInfo[] = [];
        if (stored) {
          try {
            const parse = JSON.parse(stored) as SerialPortInfo[];
            if (Array.isArray(parse)) {
              savedScanners = parse;
            }
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error parsing saved scanners:', error);
          }
        }

        for (const port of newPorts) {
          try {
            const portInfo = port.getInfo();
            // Only add scanner if it's in the saved scanners list
            const isSaved = savedScanners.some((savedScanner) =>
              portInfoEquals(portInfo, savedScanner),
            );

            if (isSaved) {
              await addScanner(port);
            }
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error(error);
          }
        }
      }
    }

    void loadPorts();
    return () => {
      loading = false;
    };
  }, [addScanner]);

  // Called when devices change - for connect and disconnect events after page load
  useEffect(() => {
    function deviceConnected(e: Event) {
      // Add |e.target| to the UI or automatically connect.
      const target = e.target as SerialPort;
      const info = target.getInfo();
      if (devices.some((t) => portInfoEquals(t.info, info))) {
        void addScanner(target);
      }
    }

    function deviceDisconnect(e: Event) {
      // Remove |e.target| from the UI. If the device was open the disconnection can also be observed as a stream error.
      const target = e.target as SerialPort;
      const info = target.getInfo();
      setDevices((old) =>
        old.map((t) => {
          if (portInfoEquals(t.info, info)) {
            return { ...t, port: undefined };
          }

          return t;
        }),
      );
    }

    navigator.serial?.addEventListener('connect', deviceConnected);
    navigator.serial?.addEventListener('disconnect', deviceDisconnect);

    return () => {
      navigator.serial?.removeEventListener('connect', deviceConnected);
      navigator.serial?.removeEventListener('disconnect', deviceDisconnect);
    };
  }, [addScanner, devices]);

  const removeScanner = useCallback(
    (info: SerialPortInfo) => {
      try {
        for (const device of devices) {
          if (portInfoEquals(device.info, info)) {
            void device.port?.forget();
            void device.port?.close();
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      }

      setDevices((old) => old.filter((t) => !portInfoEquals(t.info, info)));
    },
    [devices],
  );

  return useMemo<ScannerContextProps>(
    () => ({
      scanListeners,
      scanners: devices,
      addScanner,
      setListeners: setScanListeners,
      removeScanner,
    }),
    [devices, scanListeners, removeScanner, addScanner],
  );
};

export const useScannerContext = (): ScannerContextProps => {
  return useContext(ScannerContext)!;
};
